import { isAxiosError } from 'axios'
import api, {
    MutationRequestOptions,
    getMutationRequestConfig,
} from '../utils/axiosConfig'
import {
    StatisticsApiResponse,
    StatisticsDefaultRangeResponse,
    StatisticsExportJob,
    StatisticsExportJobDownload,
    StatisticsFilters,
} from '../types/statistics'
import { languageService } from './localeService'
import { extractApiErrorDetail } from '../utils/apiErrorI18n'

interface StatisticsParams {
    startDate?: string
    endDate?: string
    startTime?: number
    endTime?: number
    projectIds?: string
    regionIds?: string
    deviceIds?: string
    pestTypes?: string
    analysisFeature?: string
    deviceScope?: string
    lang?: string
}

export interface StatisticsRequestOptions {
    signal?: AbortSignal
    timeout?: number
}

export const STATISTICS_REQUEST_TIMEOUT_MS = 30000

const joinFilterValues = (values: Array<number | string>): string | undefined =>
    values.length > 0 ? values.join(',') : undefined

type StatisticsDateValue = string | number

const buildStatisticsParams = (
    startDateOrStartTime?: StatisticsDateValue,
    endDateOrEndTime?: StatisticsDateValue,
    filters?: StatisticsFilters,
    analysisFeature?: string
): StatisticsParams => {
    const params: StatisticsParams = {}
    if (typeof startDateOrStartTime === 'string') {
        if (startDateOrStartTime) params.startDate = startDateOrStartTime
        if (typeof endDateOrEndTime === 'string' && endDateOrEndTime) {
            params.endDate = endDateOrEndTime
        }
    } else {
        if (startDateOrStartTime) params.startTime = startDateOrStartTime
        if (typeof endDateOrEndTime === 'number' && endDateOrEndTime) {
            params.endTime = endDateOrEndTime
        }
    }
    if (analysisFeature) params.analysisFeature = analysisFeature
    if (filters) {
        params.projectIds = joinFilterValues(filters.projectIds)
        params.regionIds = joinFilterValues(filters.regionIds)
        params.deviceIds = joinFilterValues(filters.deviceIds)
        params.pestTypes = joinFilterValues(filters.pestTypes)
        params.deviceScope = filters.deviceScope
    }
    return params
}

const downloadBlob = (blob: Blob, filename: string) => {
    const downloadUrl = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = downloadUrl
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(downloadUrl)
}

const downloadUrl = (url: string, filename: string) => {
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    link.rel = 'noopener'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
}

const LOCALIZED_EXPORT_FILENAME_PREFIX: Record<string, string> = {
    'zh-CN': '蚊虫趋势分析',
    'zh-HK': '蚊蟲趨勢分析',
    en: 'trend_analysis_export',
}

const getLocalizedExportFallbackFilename = (language: string): string => {
    const prefix =
        LOCALIZED_EXPORT_FILENAME_PREFIX[language] ||
        LOCALIZED_EXPORT_FILENAME_PREFIX['zh-CN']

    return `${prefix}_${new Date().toISOString().split('T')[0]}.xlsx`
}

const parseContentDispositionFilename = (
    dispositionHeader?: string
): string | undefined => {
    if (!dispositionHeader) {
        return undefined
    }

    const encodedMatch = dispositionHeader.match(/filename\*=UTF-8''([^;]+)/i)
    if (encodedMatch?.[1]) {
        try {
            return decodeURIComponent(encodedMatch[1])
        } catch (error) {
            console.warn('Failed to decode statistics export filename:', error)
        }
    }

    const plainMatch = dispositionHeader.match(/filename="?([^"]+)"?/i)
    return plainMatch?.[1]
}

export const getStatisticsData = async (
    startDateOrStartTime?: StatisticsDateValue,
    endDateOrEndTime?: StatisticsDateValue,
    filters?: StatisticsFilters,
    analysisFeature?: string,
    requestOptions?: StatisticsRequestOptions
): Promise<StatisticsApiResponse> => {
    try {
        const params = buildStatisticsParams(
            startDateOrStartTime,
            endDateOrEndTime,
            filters,
            analysisFeature
        )
        const { data } = await api.get('/statistics/', {
            params,
            signal: requestOptions?.signal,
            timeout: requestOptions?.timeout,
        })
        return data
    } catch (error) {
        if (!(isAxiosError(error) && error.code === 'ERR_CANCELED')) {
            console.error('Failed to fetch statistics data:', error)
        }
        throw error
    }
}

export const getStatisticsDefaultRange =
    async (): Promise<StatisticsDefaultRangeResponse> => {
        try {
            const { data } = await api.get('/statistics/default-range')
            return data
        } catch (error) {
            console.error('Failed to fetch statistics default range:', error)
            throw error
        }
    }

export const exportStatisticsData = async (
    startDateOrStartTime?: StatisticsDateValue,
    endDateOrEndTime?: StatisticsDateValue,
    filters?: StatisticsFilters,
    analysisFeature?: string
): Promise<void> => {
    const language = languageService.getCurrentLanguage()
    const params = {
        ...buildStatisticsParams(
            startDateOrStartTime,
            endDateOrEndTime,
            filters,
            analysisFeature
        ),
        lang: language,
    }
    const response = await api.get('/statistics/export', {
        params,
        responseType: 'blob',
    })

    const blob = new Blob([response.data], {
        type:
            response.headers['content-type'] ||
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    })
    const dispositionHeader = response.headers['content-disposition']
    const matchedFileName = parseContentDispositionFilename(dispositionHeader)

    downloadBlob(
        blob,
        matchedFileName || getLocalizedExportFallbackFilename(language)
    )
}

export interface StatisticsPredictionExportLogPayload {
    start_time?: number
    end_time?: number
    project_ids: number[]
    region_ids: number[]
    device_ids: string[]
    pest_types: string[]
    prediction_days: number
}

export const recordStatisticsPredictionExportLog = async (
    payload: StatisticsPredictionExportLogPayload,
    options?: MutationRequestOptions
): Promise<void> => {
    await api.post(
        '/statistics/prediction-export-log',
        payload,
        getMutationRequestConfig(options)
    )
}

export interface CreateStatisticsProjectReportExportPayload {
    project_ids: number[]
    region_ids: number[]
    device_ids: string[]
    pest_types: string[]
    start_time?: number
    end_time?: number
    start_date?: string
    end_date?: string
    lang?: string
    analysis_feature?: string
}

interface StatisticsExportJobResult {
    success: boolean
    job?: StatisticsExportJob
    errorMessage?: string
}

interface StatisticsExportJobListResult {
    success: boolean
    jobs: StatisticsExportJob[]
    errorMessage?: string
}

interface StatisticsExportJobDownloadResult {
    success: boolean
    download?: StatisticsExportJobDownload
    errorMessage?: string
}

const extractStatisticsJobErrorMessage = (
    error: unknown
): string | undefined => {
    const detail = extractApiErrorDetail(error)
    if (detail) {
        return detail
    }

    return error instanceof Error ? error.message : undefined
}

export const createStatisticsProjectReportExportJob = async (
    payload: CreateStatisticsProjectReportExportPayload,
    options?: MutationRequestOptions
): Promise<StatisticsExportJobResult> => {
    try {
        const language = languageService.getCurrentLanguage()
        const response = await api.post<StatisticsExportJob>(
            '/statistics/project-report-exports',
            {
                ...payload,
                lang: payload.lang || language,
            },
            getMutationRequestConfig(options)
        )
        return { success: true, job: response.data }
    } catch (error) {
        console.error('Failed to create statistics export job:', error)
        return {
            success: false,
            errorMessage: extractStatisticsJobErrorMessage(error),
        }
    }
}

export const getStatisticsProjectReportExportJob = async (
    jobId: string
): Promise<StatisticsExportJobResult> => {
    try {
        const response = await api.get<StatisticsExportJob>(
            `/statistics/project-report-exports/${jobId}`
        )
        return { success: true, job: response.data }
    } catch (error) {
        console.error(`Failed to fetch statistics export job ${jobId}:`, error)
        return {
            success: false,
            errorMessage: extractStatisticsJobErrorMessage(error),
        }
    }
}

export const listStatisticsProjectReportExportJobs =
    async (): Promise<StatisticsExportJobListResult> => {
        try {
            const response = await api.get<StatisticsExportJob[]>(
                '/statistics/project-report-exports'
            )
            return { success: true, jobs: response.data }
        } catch (error) {
            console.error('Failed to fetch statistics export jobs:', error)
            return {
                success: false,
                jobs: [],
                errorMessage: extractStatisticsJobErrorMessage(error),
            }
        }
    }

export const getStatisticsProjectReportExportJobDownload = async (
    jobId: string
): Promise<StatisticsExportJobDownloadResult> => {
    try {
        const response = await api.get<StatisticsExportJobDownload>(
            `/statistics/project-report-exports/${jobId}/download`
        )
        return { success: true, download: response.data }
    } catch (error) {
        console.error(
            `Failed to fetch statistics export download ${jobId}:`,
            error
        )
        return {
            success: false,
            errorMessage: extractStatisticsJobErrorMessage(error),
        }
    }
}

export const triggerStatisticsProjectReportExportDownload = (
    download: StatisticsExportJobDownload
): void => {
    downloadUrl(download.download_url, download.file_name)
}
